import { DropdownOption } from '@acceso/design-system';
import { useMemo } from 'react';

import { CollapseAllButton } from '@/components/CollapseAllButton/CollapseAllButton';
import { BroadcastActionsBar } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/components/broadcastComponents/BroadcastActionsBar';
import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import {
  FrameActionType,
  PresenceFor,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useGroupedPresences } from '@/hooks/use-grouped-presences';
import { PresenceTypeEnum, TPostPresenceItem } from '@/types/presences';

type VideoPresencesManagementBarProps<T extends TPostPresenceItem> = {
  items: PresenceFor<T>[];
  onDelete?: (item: PresenceFor<T>, silently?: boolean) => void;
  onUpdateInline?: (item: T) => void;
  onPreview: (item: PresenceFor<T>, frame?: FrameActionType) => void;
  groupOptions: DropdownOption[];
  presenceType: PresenceTypeEnum;
};

export function VideoPresencesManagementBar<T extends TPostPresenceItem>({
  items,
  onPreview,
  onDelete,
  onUpdateInline,
  groupOptions,
  presenceType,
}: VideoPresencesManagementBarProps<T>) {
  const entityIdKey = presenceType === PresenceTypeEnum.PEOPLE ? 'people_id' : 'logo_id';
  const {
    areGroupsOnGroupedLogos,
    onChangeCriteria,
    onChangeSortDirection,
    sortDirection,
    onChangeAutoplayClipsMode,
  } = useGroupedPresences(items as any, onPreview as any, entityIdKey as any);
  const {
    groupedPresences,
    expandAllPresences,
    setExpandAllPresences,
    rowExpandOverrides,
    getRowExpandDesired,
    clearRowExpandOverrides,
  } = useBroadcastScheduleContext();

  const areAllGroupsExpanded = useMemo(() => {
    const expandableRows = groupedPresences.filter(
      item => (item as any).children?.length > 0 && (item as any).id != null
    );
    if (expandableRows.length === 0) return false;
    return expandableRows.every(item => getRowExpandDesired((item as any).id, expandAllPresences));
  }, [groupedPresences, expandAllPresences, rowExpandOverrides, getRowExpandDesired]);

  const handleToggleCollapseAll = () => {
    setExpandAllPresences(!areAllGroupsExpanded);
    clearRowExpandOverrides();
  };

  return (
    <div className="relative">
      <BroadcastActionsBar<T>
        groupedPresences={groupedPresences}
        onDelete={onDelete as any}
        onUpdateInline={onUpdateInline}
        onChangeCriteria={onChangeCriteria}
        onChangeSortDirection={onChangeSortDirection}
        sortDirection={sortDirection}
        onChangeAutoplayClipsMode={onChangeAutoplayClipsMode}
        groupOptions={groupOptions}
        presenceType={presenceType}
      />
      {areGroupsOnGroupedLogos && (
        <div
          className="absolute right-0"
          data-id="video-presences-management-bar-collapse-all-button"
        >
          <CollapseAllButton isExpanded={areAllGroupsExpanded} onClick={handleToggleCollapseAll} />
        </div>
      )}
    </div>
  );
}
